import {OrderModel} from '../../order/order.model';
import {ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal} from "@angular/core";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {navParam} from "../../navigation/navigation-params";
import {Page} from "../../navigation/page";
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence} from "../../buttons/button/button";
import {OrderingContextModel} from "../ordering-context.model";
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {DatePipe, NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {SelectionFieldComponent} from '../../inputs/selection-field/selection-field.component';
import {SpinnerComponent} from "../../ui-elements/spinner/spinner.component";
import {
    AvailableOrderTimeData,
    AvailableOrderTimesGroupData,
    AvailableOrderTimesResponseData
} from '../../order/available-order-times.service';
import {UnavailabilityReason} from '../../order/order-initiation.service';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-order-time-selection-page",
    templateUrl: "./order-time-selection-page.component.html",
    styleUrl: "./order-time-selection-page.component.scss",
    providers: [{provide: Page, useExisting: OrderTimeSelectionPageComponent}],
    imports: [
        ButtonComponent,
        ContentMessageComponent,
        ModalPageLayoutComponent,
        NgClass,
        PageFooterControlDirective,
        ResponsiveDirective,
        SelectionFieldComponent,
        SpinnerComponent,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderTimeSelectionPageComponent extends Page {
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private orderModel: OrderModel = inject(OrderModel);

    ButtonProminence = ButtonProminence;
    MessageLevel = MessageLevel;

    static readonly MAX_DAYS_ORDER_LATER: number = 1;

    private readonly hideAsap: Signal<boolean> = navParam.required<boolean>("hideAsap");

    public readonly orderDate: WritableSignal<Date> = signal<Date>(undefined);
    public readonly orderTime: WritableSignal<Date> = signal<Date>(undefined);
    public readonly showAsap: WritableSignal<boolean> = signal<boolean>(
        this.orderingContextModel.canShowAsapSelectionTime() && !this.hideAsap() &&
        !(this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().closed));
    public readonly showDateSelect: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly showTimeSelect: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly showEarliestDate: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly orderTimeIsCarryOver: WritableSignal<boolean> = signal<boolean>(false);
    public readonly loadingTimes: WritableSignal<boolean> = signal<boolean>(true);
    public readonly availableTimes: WritableSignal<AvailableOrderTimesResponseData> = signal<AvailableOrderTimesResponseData>(undefined);

    public readonly earliestDate: Signal<Date> = computed<Date>(() => this.availableTimes()?.earliestLocalDate ? this.parseDate(this.availableTimes()?.earliestLocalDate) : undefined);
    public readonly formattedEarliestLocalDate: Signal<string> = computed<string>(() => this.earliestDate() ? this.formatDate(this.earliestDate()) : undefined);

    public readonly showStoreClosed: Signal<boolean> = computed<boolean>(() =>
        !this.showAsap() && this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().closed && this.orderingContextModel.canShowAsapSelectionTime());

    public readonly canOrderLater: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.currentStore() ? this.orderingContextModel.currentStore().orderAheadLaterStore || this.showEarliestDate() : this.orderingContextModel.hasAnyStoresWithOrderLater());

    public readonly canOrderLaterTime: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.currentStore() ? this.orderingContextModel.currentStore().orderAheadLaterTimeStore : this.orderingContextModel.hasAnyStoresWithOrderLaterTime());

    public readonly timesAvailable: Signal<boolean> = computed<boolean>(() =>
        !this.loadingTimes() && this.availableTimes() && this.availableTimes().specificTimeGroups.length > 0);

    public readonly noTimesMessage: Signal<string> = computed<string>(() =>
        this.isToday(this.orderDate()) ? (this.orderingContextModel.currentStore() ? "woa.message.order-time.noTimesTodayForStore" : "woa.message.order-time.noTimesToday")
            : (this.orderingContextModel.currentStore() ? "woa.message.order-time.noTimesForStore" : "woa.message.order-time.noTimes"));

    availableDates: Date[] = [];

    constructor() {
        super();

        if (this.orderingContextModel.expectedCompletion()) {
            this.setOrderDate(new Date(this.orderingContextModel.expectedCompletion()));
            this.orderTime.set(new Date(this.orderingContextModel.expectedCompletion()));
        }

        void this.loadAvailableTimes();
    }

    async loadAvailableTimes(): Promise<void> {
        if (!this.orderDate()) {
            this.setOrderDate(this.configurationModel.now);
        }

        // Load available times for the selected date
        const dateRequired: string = new DatePipe("en-US").transform(this.orderDate(), "yyyy-MM-dd");
        this.loadingTimes.set(true);
        this.orderTimeIsCarryOver.set(false);
        try {
            this.availableTimes.set(await this.orderModel.getAvailableOrderTimes(dateRequired));
            this.loadingTimes.set(false);
            if (this.orderTime()) {
                if (!this.availableTimes().specificTimeGroups.some((group: AvailableOrderTimesGroupData) => {
                    return group.specificTimes.some((time: AvailableOrderTimeData) => {
                        if (this.sameTime(this.orderTime(), this.parseTime(time.time)) && time.available) {
                            this.orderTimeIsCarryOver.set(time.carryOver);
                            return true;
                        }
                        return false;
                    });
                })) {
                    this.orderTime.set(undefined);
                }
            } else {
                this.availableTimes().specificTimeGroups.some((group: AvailableOrderTimesGroupData) => {
                    return group.specificTimes.some((time: AvailableOrderTimeData) => {
                        if (time.available) {
                            this.orderTime.set(this.parseTime(time.time));
                            this.orderTimeIsCarryOver.set(time.carryOver);
                            return true;
                        }
                        return false;
                    });
                });
            }
        } catch (error) {
            this.availableTimes.set(null);
            this.loadingTimes.set(false);
        }

        // Load available dates from the available times
        this.availableDates = [];
        let dateChanged: boolean = false;
        if (this.canOrderLater()) {

            // Iterate the dates from today until the order ahead limit
            const maxOrderLaterDays: number = this.orderingContextModel.currentStore()?.maxOrderLaterDays ?? this.configurationModel.app()?.maxDaysOrderLater ?? OrderTimeSelectionPageComponent.MAX_DAYS_ORDER_LATER;
            let date: Date = this.configurationModel.now;
            date.setHours(0, 0, 0, 0);
            for (let i = 0; i <= maxOrderLaterDays; i++) {
                const localDate: string = new DatePipe("en-US").transform(date, "yyyy-MM-dd");

                // Only show available dates on or after the earliest available date
                if (!this.availableTimes()?.earliestLocalDate || localDate >= this.availableTimes()?.earliestLocalDate) {
                    this.availableDates.push(date);
                    if (!this.orderDate()) {
                        this.setOrderDate(date);
                        dateChanged = true;
                    }
                } else {

                    // If today's date is not available, then we can't choose "ASAP" and we must have a product with a lead time.
                    if (i == 0) {
                        this.showAsap.set(false);
                        this.showEarliestDate.set(true);
                    }

                    // If the currently selected date is not available, deselect it
                    if (this.orderDate() && this.sameDate(this.orderDate(), date)) {
                        this.orderDate.set(undefined);
                        dateChanged = true;
                    }
                }

                date = new Date(date.getTime());
                date.setDate(date.getDate() + 1);
            }
        } else if (!this.isToday(this.orderDate())) {

            // If we can't order ahead and the currently selected date is in the future, default it to today.
            if (!this.sameDate(this.orderDate(), this.configurationModel.now)) {
                dateChanged = true;
            }
            this.setOrderDate(this.configurationModel.now);
        }

        // If we have found no available dates, then either the earliest available date falls outside the normal order ahead limit due to a long product lead time,
        // or order ahead is not available. Either way, we want to offer the earliest date as the sole option.
        if (!this.availableDates.length && this.earliestDate()) {
            this.availableDates.push(this.earliestDate());
            if (!this.sameDate(this.orderDate(), this.earliestDate())) {
                dateChanged = true;
            }
            this.setOrderDate(this.earliestDate());

            // The server returns an "earliest date" of today if there are no product lead times,
            // but we don't want to display it in a message in that case.
            if (!this.isToday(this.earliestDate())) {
                this.showEarliestDate.set(true);
            }
        }

        // If the order date has changed, we need to reload the available times.
        if (dateChanged) {
            await this.loadAvailableTimes();
        }
    }

    formatDate(date: Date): string {
        return new DatePipe("en-US").transform(date, "EEE, MMM d yyyy");
    }

    formatTime(date: Date): string {
        return new DatePipe("en-US").transform(date, "h:mma").toUpperCase();
    }

    parseDate(dateSt: string): Date {
        const parts: string[] = dateSt.split("-");
        let year: number = parseInt(parts[0]);
        let month: number = parseInt(parts[1]);
        const day: number = parseInt(parts[2]);
        const date: Date = this.configurationModel.now;
        date.setFullYear(year, month - 1, day);
        date.setHours(0, 0, 0, 0);
        return date;
    }

    parseTime(timeSt: string): Date {
        const parts: string[] = timeSt.split(":");
        let hours: number = parseInt(parts[0]);
        const minutes: number = parseInt(parts[1]);
        if (hours < 12 && timeSt.toLowerCase().endsWith("pm")) {
            hours += 12;
        } else if (hours === 12 && timeSt.toLowerCase().endsWith("am")) {
            hours = 0;
        }
        const date: Date = this.configurationModel.now;
        date.setHours(hours, minutes, 0, 0);
        return date;
    }

    setOrderDate(date: Date): void {
        const orderDate = date;
        orderDate.setHours(0, 0, 0, 0);
        this.orderDate.set(orderDate);
    }

    sameTime(time1: Date, time2: Date): boolean {
        return time1.getHours() === time2.getHours() && time1.getMinutes() === time2.getMinutes();
    }

    sameDate(date1: Date, date2: Date): boolean {
        return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
    }

    isToday(date: Date): boolean {
        return this.sameDate(date, this.configurationModel.now);
    }

    dateFieldValue(date: Date): string {
        return this.isToday(date) ? "woa.button.order-time.laterToday" : "'" + this.formatDate(date) + "'";
    }

    timeFieldValue(): string {
        return this.loadingTimes() ? "woa.button.order-time.loadingTimes"
            : !this.timesAvailable() ? "woa.button.order-time.selectTime"
                : this.orderTime() ? "'" + this.formatTime(this.orderTime()) + (this.orderTimeIsCarryOver() ? " *" : "") + "'"
                    : "woa.button.order-time.selectTime";
    }

    timeSlotLabel(time: AvailableOrderTimeData, forList: boolean = false): string {
        return time.available ? "woa.label.order-time.available"
            : time.unavailabilityReason === UnavailabilityReason.UNAVAILABLE_CLOSED ? "woa.label.order-time.closed"
                : time.unavailabilityReason === UnavailabilityReason.UNAVAILABLE_CLOSING ? "woa.label.order-time.closing"
                    : time.unavailabilityReason === UnavailabilityReason.UNAVAILABLE_PAST ? "woa.label.order-time.past"
                        : time.unavailabilityReason === UnavailabilityReason.UNAVAILABLE_CAPACITY && forList ? "woa.label.order-time.listCapacity"
                            : time.unavailabilityReason === UnavailabilityReason.UNAVAILABLE_CAPACITY ? "woa.label.order-time.capacity"
                                : "";
    }

    showTimeInList(time: AvailableOrderTimeData): boolean {
        return time.available || time.unavailabilityReason === UnavailabilityReason.UNAVAILABLE_CAPACITY;
    }

    closeSelections() {
        this.showDateSelect.set(false);
        this.showTimeSelect.set(false);
    }

    toggleDateSelection(): void {
        this.showDateSelect.set(!this.showDateSelect());
        if (this.showDateSelect()) {
            this.showTimeSelect.set(false);
        }
    }

    toggleTimeSelection(): void {
        this.showTimeSelect.set(!this.showTimeSelect());
        if (this.showTimeSelect()) {
            this.showDateSelect.set(false);
        }
    }

    selectDate(date: Date): void {
        this.setOrderDate(date);
        this.showDateSelect.set(false);
        void this.loadAvailableTimes();
    }

    selectTime(time: AvailableOrderTimeData): void {
        this.orderTime.set(this.parseTime(time.time));
        this.orderTimeIsCarryOver.set(time.carryOver);
        this.showTimeSelect.set(false);
    }

    confirmTime(): void {
        const date: Date = this.orderDate();
        date.setHours(this.orderTime().getHours(), this.orderTime().getMinutes(), 0, 0);
        this.orderingContextModel.expectedCompletion.set(date);
        if (this.orderTimeIsCarryOver()) {
            this.orderingContextModel.expectedCompletion().setDate(this.orderingContextModel.expectedCompletion().getDate() + 1);
        }
        void this.dismiss(true);
    }

    confirmASAP(): void {
        this.orderingContextModel.expectedCompletion.set(undefined);
        void this.dismiss(true);
    }
}
